import React, { useEffect, useRef, useState } from 'react'

import LoadingView from 'pages/Loading/LoadingView'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { getKycSessionStatus, KycStatus } from 'services/kyc.service'

type DisplayStatus = 'Approved' | 'Declined' | 'In Review' | 'generic'

const CICLOGREEN_TEAL = '#00c8b4'

interface IconProps {
  className?: string
  style?: React.CSSProperties
}

const CheckIcon: React.FC<IconProps> = ({ className, style }) => (
  <svg
    className={className}
    style={style}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
)

const ClockIcon: React.FC<IconProps> = ({ className, style }) => (
  <svg
    className={className}
    style={style}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)

const XIcon: React.FC<IconProps> = ({ className, style }) => (
  <svg
    className={className}
    style={style}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
)

const InfoIcon: React.FC<IconProps> = ({ className, style }) => (
  <svg
    className={className}
    style={style}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)

interface StatusConfig {
  icon: React.FC<IconProps>
  iconBg: string
  iconColor: string
  titleKey: string
  messageKey: string
  detailKey?: string
}

const STATUS_CONFIG: Record<DisplayStatus, StatusConfig> = {
  Approved: {
    icon: CheckIcon,
    iconBg: '#00c8b415',
    iconColor: CICLOGREEN_TEAL,
    titleKey: 'approved.title',
    messageKey: 'approved.message',
  },
  Declined: {
    icon: XIcon,
    iconBg: '#EF444420',
    iconColor: '#EF4444',
    titleKey: 'declined.title',
    messageKey: 'declined.message',
    detailKey: 'declined.detail',
  },
  'In Review': {
    icon: ClockIcon,
    iconBg: '#F59E0B20',
    iconColor: '#F59E0B',
    titleKey: 'inReview.title',
    messageKey: 'inReview.message',
    detailKey: 'inReview.detail',
  },
  generic: {
    icon: InfoIcon,
    iconBg: '#6B728020',
    iconColor: '#6B7280',
    titleKey: 'generic.title',
    messageKey: 'generic.message',
  },
}

const mapKycStatusToDisplay = (status: KycStatus): DisplayStatus => {
  switch (status) {
    case 'APPROVED':
      return 'Approved'
    case 'DECLINED':
      return 'Declined'
    case 'IN_REVIEW':
      return 'In Review'
    case 'INITIATED':
    case 'IN_PROGRESS':
    case 'ABANDONED':
    case 'EXPIRED':
    default:
      return 'generic'
  }
}

const KycCallback: React.FC = () => {
  const { t } = useTranslation('kycCallback')
  const [searchParams] = useSearchParams()

  const [loading, setLoading] = useState(true)
  const [displayStatus, setDisplayStatus] = useState<DisplayStatus>('generic')
  const hasFetched = useRef(false)

  const verificationSessionId = searchParams.get('verificationSessionId')

  useEffect(() => {
    if (hasFetched.current) return

    const fetchSessionStatus = async () => {
      if (!verificationSessionId) {
        setLoading(false)
        setDisplayStatus('generic')
        return
      }

      hasFetched.current = true

      try {
        const axiosCall = getKycSessionStatus(verificationSessionId)
        const response = await axiosCall.call
        const backendStatus = response.data?.status

        if (backendStatus) {
          setDisplayStatus(mapKycStatusToDisplay(backendStatus))
        } else {
          setDisplayStatus('generic')
        }
      } catch (error) {
        console.error('[KycCallback] Failed to fetch session status', {
          verificationSessionId,
          error,
        })
        setDisplayStatus('generic')
      } finally {
        setLoading(false)
      }
    }

    fetchSessionStatus()
  }, [verificationSessionId])

  if (loading) {
    return <LoadingView t={t} />
  }

  const config = STATUS_CONFIG[displayStatus]
  const Icon = config.icon

  return (
    <div className="h-[100dvh] flex items-center justify-center bg-gray-50 overflow-hidden px-4">
      <div className="max-w-md w-full bg-white p-6 rounded-xl shadow-md space-y-6">
        <div className="flex justify-center">
          <img
            alt="Ciclogreen Logo"
            src={`${process.env.REACT_APP_URL_S3}/images/logo.png`}
            width={180}
            height={28}
            className="mx-auto"
          />
        </div>

        <div className="flex flex-col items-center text-center space-y-3">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: config.iconBg }}
          >
            <Icon className="w-8 h-8" style={{ color: config.iconColor }} />
          </div>

          <h2 className="text-xl font-bold text-gray-900">
            {t(config.titleKey)}
          </h2>
          <p className="text-gray-600 text-sm">{t(config.messageKey)}</p>

          {config.detailKey && (
            <p className="text-gray-400 text-xs">{t(config.detailKey)}</p>
          )}
        </div>

        <div className="space-y-3">
          <a
            href="/app?target=ciclobono"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex justify-center py-3 px-4 rounded-full text-sm font-bold text-white hover:brightness-90 transition-all focus:outline-none no-underline"
            style={{ backgroundColor: CICLOGREEN_TEAL }}
          >
            {t('backToApp')}
          </a>
        </div>
      </div>
    </div>
  )
}

export default KycCallback
